import { motion } from "framer-motion";
import { Video, Images, MessageCircle, Wand2, Zap, Flag, ShieldCheck, Ban, Flag as Report, UserCog } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

const reveal = {
  hidden: { opacity: 0, y: 36 },
  show: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE, delay: i * 0.06 } }),
};

const IMG_VIDEO = "https://images.unsplash.com/photo-1623344804003-348e7c1e4aba";
const IMG_STORY = "https://images.pexels.com/photos/8242767/pexels-photo-8242767.jpeg";

const Card = ({ children, className = "", i }) => (
  <motion.div
    custom={i}
    variants={reveal}
    initial="hidden"
    whileInView="show"
    viewport={{ once: true, amount: 0.25 }}
    whileHover={{ y: -6 }}
    data-testid="feature-card"
    className={`group relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] p-7 backdrop-blur-xl transition-colors duration-300 hover:border-indril-saffron/40 ${className}`}
  >
    {children}
  </motion.div>
);

const Icon = ({ children, tone }) => (
  <span className={`mb-4 flex h-11 w-11 items-center justify-center rounded-2xl ${tone}`}>{children}</span>
);

export const FeatureSections = () => (
  <section id="features" className="relative mx-auto max-w-6xl scroll-mt-24 px-6 py-24" data-testid="features-section">
    <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, ease: EASE }} className="mb-14 max-w-2xl">
      <span className="text-xs uppercase tracking-[0.3em] text-indril-saffron">Everything in one home</span>
      <h2 className="mt-4 font-display text-4xl font-semibold tracking-tight text-white sm:text-5xl">Built for the way <span className="text-tricolor">India creates</span>.</h2>
    </motion.div>

    <div className="grid grid-cols-1 gap-5 md:grid-cols-6">
      <Card i={0} className="md:col-span-4 md:row-span-2 min-h-[22rem]">
        <img src={IMG_VIDEO} alt="Short video creator" loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-30 transition-transform duration-700 group-hover:scale-105" />
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/70 to-transparent" />
        <div className="relative flex h-full flex-col justify-end">
          <Icon tone="bg-indril-saffron/15 text-indril-saffron"><Video className="h-5 w-5" /></Icon>
          <h3 className="font-display text-3xl font-semibold text-white">Short Videos</h3>
          <p className="mt-2 max-w-md text-white/60">Fast video discovery, smooth playback and a creator-focused feed tuned to Indian culture.</p>
        </div>
      </Card>

      <Card i={1} className="md:col-span-2">
        <Icon tone="bg-indril-pink/15 text-indril-pink"><Images className="h-5 w-5" /></Icon>
        <h3 className="font-display text-xl font-semibold text-white">Photos &amp; Stories</h3>
        <p className="mt-2 text-sm text-white/60">Share moments with creative tools and an expressive story experience.</p>
      </Card>

      <Card i={2} className="md:col-span-2">
        <Icon tone="bg-indril-blue/15 text-indril-blue"><MessageCircle className="h-5 w-5" /></Icon>
        <h3 className="font-display text-xl font-semibold text-white">Private Messaging</h3>
        <p className="mt-2 text-sm text-white/60">Personal conversations with media sharing and clear privacy controls.</p>
      </Card>

      <Card i={3} className="md:col-span-3 min-h-[15rem]">
        <img src={IMG_STORY} alt="Creator tools" loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-20 transition-transform duration-700 group-hover:scale-105" />
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/60 to-transparent" />
        <div className="relative flex h-full flex-col justify-end">
          <Icon tone="bg-indril-green/15 text-indril-green"><Wand2 className="h-5 w-5" /></Icon>
          <h3 className="font-display text-2xl font-semibold text-white">Unified Creator Tools</h3>
          <p className="mt-2 max-w-sm text-sm text-white/60">Video, photo, story and voice editing with creator analytics and future monetization eligibility.</p>
        </div>
      </Card>

      <Card i={4} className="md:col-span-3 min-h-[15rem]">
        <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-indril-saffron/20 blur-3xl" />
        <div className="relative flex h-full flex-col justify-between">
          <Icon tone="bg-white/5 text-indril-saffron"><Zap className="h-5 w-5" /></Icon>
          <div>
            <h3 className="font-display text-2xl font-semibold text-white">Fast Performance</h3>
            <p className="mt-2 max-w-sm text-sm text-white/60">Optimized media, a smooth feed and fast navigation across every device.</p>
          </div>
          <div className="mt-4 flex items-center gap-2">
            <Flag className="h-4 w-4 text-indril-green" />
            <span className="text-sm text-white/70">Built for India and a diverse creator community.</span>
          </div>
        </div>
      </Card>
    </div>
  </section>
);

const SAFETY = [
  { icon: <Ban className="h-5 w-5" />, title: "Blocking", desc: "Instantly block accounts and control who can reach you." },
  { icon: <Report className="h-5 w-5" />, title: "Reporting", desc: "Report content or accounts with a clear, quick flow." },
  { icon: <UserCog className="h-5 w-5" />, title: "Account Controls", desc: "Privacy-first choices over your profile, content and audience." },
  { icon: <ShieldCheck className="h-5 w-5" />, title: "Community Moderation", desc: "Policies and review processes that keep the community healthy." },
];

export const SafetySection = () => (
  <section id="safety" className="relative mx-auto max-w-6xl scroll-mt-24 px-6 py-20" data-testid="safety-section">
    <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, ease: EASE }} className="mb-12 max-w-2xl">
      <span className="text-xs uppercase tracking-[0.3em] text-indril-green">Privacy &amp; Safety</span>
      <h2 className="mt-4 font-display text-4xl font-semibold tracking-tight text-white sm:text-5xl">Safety and privacy, by design.</h2>
      <p className="mt-4 text-white/55">Tools that put you in control — with community moderation and privacy-first choices at the core.</p>
    </motion.div>
    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
      {SAFETY.map((s, i) => (
        <Card key={s.title} i={i}>
          <Icon tone="bg-indril-green/15 text-indril-green">{s.icon}</Icon>
          <h3 className="font-display text-lg font-semibold text-white">{s.title}</h3>
          <p className="mt-2 text-sm text-white/60">{s.desc}</p>
        </Card>
      ))}
    </div>
  </section>
);
